import { useEffect, useState } from 'react'
import { useIsAdmin } from '@/components/admin-protected-route'
import { posthog } from '@/lib/posthog'

/**
 * Check if a feature flag is enabled for the current user.
 * Admins always bypass feature flags (returns true) unless
 * `skipAdminBypass` is set, which returns the raw PostHog value
 * for global on/off flags that should affect all users equally.
 */
export function useFeatureFlag(
  flag: string,
  options?: { skipAdminBypass?: boolean },
): {
  enabled: boolean
  isLoaded: boolean
} {
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()

  const [flagState, setFlagState] = useState<{
    enabled: boolean
    isLoaded: boolean
  }>(() => {
    if (typeof window === 'undefined') {
      return { enabled: false, isLoaded: false }
    }
    const value = posthog.isFeatureEnabled(flag)
    if (value !== undefined) {
      return { enabled: value, isLoaded: true }
    }
    // Check bootstrap fallback for initial render
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bootstrap = (window as any).__POSTHOG_BOOTSTRAP__ as
      | Record<string, boolean>
      | undefined
    if (bootstrap && flag in bootstrap) {
      return { enabled: !!bootstrap[flag], isLoaded: true }
    }
    return { enabled: false, isLoaded: false }
  })

  useEffect(() => {
    return posthog.onFeatureFlags(() => {
      const value = posthog.isFeatureEnabled(flag)
      if (value !== undefined) {
        setFlagState((prev) => {
          if (prev.enabled === value && prev.isLoaded) return prev
          return { enabled: value, isLoaded: true }
        })
      }
    })
  }, [flag])

  // When skipAdminBypass is set, return PostHog value directly
  if (options?.skipAdminBypass) {
    return flagState
  }

  if (!isAdminLoaded) {
    return { enabled: false, isLoaded: false }
  }

  // Admins bypass all feature flags
  if (isAdmin) {
    return { enabled: true, isLoaded: true }
  }

  return flagState
}
